'use client';

import Link from 'next/link';
import { Laptop, LogOutIcon, Moon, MoreVerticalIcon, Sun, UserCircleIcon, UserIcon } from 'lucide-react';
import { useTheme } from 'next-themes';
import { toast } from 'sonner';

import { useApiV1Ctx } from '@/components/providers/v1-api';
import { useMeCtx } from '@/components/providers/v1-me-provider';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from '@/components/ui/sidebar';
import { logout } from '@/server/logout';

export function NavUser() {
  const { me } = useMeCtx();

  const { isMobile } = useSidebar();

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size='lg'
              className='text-foreground transition-colors data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground'
            >
              <Avatar className='h-8 w-8 rounded-lg grayscale'>
                <AvatarImage src={me?.avatar?.url ?? undefined} />
                <AvatarFallback className='rounded-lg border-1'>{me?.first_name?.charAt(0)}</AvatarFallback>
              </Avatar>
              <div className='grid flex-1 text-left text-sm leading-tight'>
                <span className='truncate font-medium'>
                  {me?.first_name} {me?.last_name}
                </span>
              </div>
              <MoreVerticalIcon className='ml-auto size-4' />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className='w-[--radix-dropdown-menu-trigger-width] max-w-72 min-w-56 rounded-lg'
            side={isMobile ? 'bottom' : 'right'}
            align='end'
            sideOffset={4}
          >
            <DropdownMenuLabel className='p-0 font-normal'>
              <div className='flex items-center gap-2 px-1 py-1.5 text-left text-sm'>
                <Avatar className='h-8 w-8 rounded-lg'>
                  <AvatarImage src={me?.avatar?.url ?? undefined} />
                  <AvatarFallback className='rounded-lg'>{me?.email?.charAt(0)}</AvatarFallback>
                </Avatar>
                <div className='w-full space-y-1 truncate'>
                  <div className='truncate text-sm leading-none font-medium'>
                    {me?.first_name} {me?.last_name}
                  </div>
                  <div className='truncate text-xs leading-none font-normal text-muted-foreground'>{me?.email}</div>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem asChild>
                <Link href='/account'>
                  <UserCircleIcon />
                  Account
                </Link>
              </DropdownMenuItem>
              <DropdownMenuTheme />
              <DropdownMenuEmployeeSwitcher />
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout}>
              <LogOutIcon />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

const themes = [
  {
    icon: Sun,
    value: 'light',
    label: 'Light',
  },
  {
    icon: Moon,
    value: 'dark',
    label: 'Dark',
  },
  {
    icon: Laptop,
    value: 'system',
    label: 'System',
  },
];

function DropdownMenuTheme() {
  const { theme, setTheme } = useTheme();
  const selectedTheme = themes.find((t) => t.value === theme);

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        {selectedTheme && <selectedTheme.icon className='mr-2 size-4' />} Theme
      </DropdownMenuSubTrigger>
      <DropdownMenuPortal>
        <DropdownMenuSubContent>
          {themes.map(({ icon: Icon, value, label }) => {
            return (
              <DropdownMenuCheckboxItem key={value} checked={value === theme} onClick={() => setTheme(value)}>
                <Icon className='size-4' />
                {label}
              </DropdownMenuCheckboxItem>
            );
          })}
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  );
}

function DropdownMenuEmployeeSwitcher() {
  const { api } = useApiV1Ctx();
  const { me } = useMeCtx();
  const { data: employees = [] } = api.employees.meOptions.useQuery();
  const mutation = api.employees.switchToEmployee.useMutation(undefined, {
    onSuccess: () => {
      toast.success('Switched to employee');
      window.location.reload();
    },
  });

  if (!employees) return null;

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <UserIcon className='mr-2 size-4' />
        Switch employee
      </DropdownMenuSubTrigger>
      <DropdownMenuPortal>
        <DropdownMenuSubContent className='min-w-56'>
          {employees.map((employee) => (
            <DropdownMenuCheckboxItem
              key={employee.id}
              checked={employee.id === me?.id}
              onCheckedChange={(checked) => {
                if (checked) {
                  mutation.mutate({ query: { employee_id: employee.id } });
                }
              }}
            >
              <div>
                <div>
                  {employee.first_name} {employee.last_name}
                </div>
                <div className='text-xs text-muted-foreground'>{employee.office_name}</div>
              </div>
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  );
}
